/**
 * The machine's own sessions and what waits on its person, by the names the desktop uses, for Overview and
 * Sessions. One Control API call when the machine has it (`companion.overview`: every repository, its members in the
 * Inbox's words, and what needs the person), else `sessions.list` and `permissions.list`. Joined with the sessions
 * its last heartbeat said are open to remote control (`device.remoteSessions`), which the platform holds, so the
 * list shows even while the machine is away. Asked every 30 seconds while a view that shows it is open, never while
 * the tab is hidden.
 */

import type { CompanionOverview, RemoteSession } from '@aioe/shared';
import { useRef } from 'react';

import { liveKey } from '../components/live/liveModel.ts';
import { useLoad } from '../hooks.ts';
import type { Permission, Session, Workbench } from './useWorkbench.ts';

export type WorkState = 'working' | 'medium' | 'idle' | 'error' | 'connected';

/** One session on the machine. `key` is the live view's (`s:` or `c:`); undefined for a session with no address. */
export interface MachineSession {
  key: string;
  title: string;
  /** What it is and where: "Chat · mju-aioe", "Backend team · writing the migration". */
  line: string;
  state: WorkState;
  words: string;
  /** Open to remote control from the web and the phone, as the heartbeat says. */
  remote?: RemoteSession;
  rootId?: string;
  waiting: number;
}

/** Something that waits on the machine's person. */
export interface MachineWait {
  id: string;
  kind: 'approval' | 'question' | 'review';
  title: string;
  line: string;
  sessionKey?: string;
  sessionId?: string;
  riskLevel?: number;
}

export interface MachineWork {
  sessions: MachineSession[];
  needsYou: MachineWait[];
  working: number;
  /** The machine answered (not just the heartbeat's copy). */
  live: boolean;
}

const STATE_OF: Record<string, WorkState> = { starting: 'working', thinking: 'working', 'calling-tools': 'working', tool_calling: 'working', 'needs-approval': 'medium', waiting_approval: 'medium', 'with-person': 'working', error: 'error', idle: 'idle' };
const REMOTE_STATE: Record<string, WorkState> = { thinking: 'working', waiting: 'medium', done: 'connected', stopped: 'idle', idle: 'idle' };
const REMOTE_WORDS: Record<string, string> = { thinking: 'Thinking', waiting: 'Waiting for you', done: 'Done', stopped: 'Stopped', idle: 'Idle' };
const MODE: Record<string, string> = { chat: 'Chat', research: 'Research', teamwork: 'Team Work', agent: 'Agent' };
const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

type Answer = { kind: 'overview'; overview: CompanionOverview } | { kind: 'basic'; sessions: Session[]; permissions: Permission[] };

export function useMachineWork(w: Workbench): { data?: MachineWork; error?: string; loading: boolean; reload: () => void } {
  // A machine from before `companion.overview` is asked the older way from then on, without trying again each time.
  const older = useRef(false);
  const answer = useLoad<Answer | undefined>(
    async () => {
      if (!w.connected) return undefined;
      if (!older.current) {
        const overview = await w.tryCall<CompanionOverview>('companion.overview', {});
        if (overview?.roots) return { kind: 'overview', overview };
        older.current = true;
      }
      const [sessions, permissions] = await Promise.all([w.call<Session[]>('sessions.list'), w.call<Permission[]>('permissions.list').catch(() => [] as Permission[])]);
      return { kind: 'basic', sessions: sessions ?? [], permissions: permissions ?? [] };
    },
    [w.connected, w.deviceId],
    w.connected ? 30_000 : undefined,
    { whenVisible: true },
  );

  const remote = w.device.data?.remoteSessions ?? [];
  const a = answer.data;
  if (!a && !remote.length) return { error: w.connected ? answer.error : undefined, loading: answer.loading, reload: answer.reload, ...(w.device.data && !w.connected ? { data: { sessions: [], needsYou: [], working: 0, live: false } } : {}) };

  const sessions: MachineSession[] = [];
  const needsYou: MachineWait[] = [];
  // A team by the name the Teams section uses; never its id.
  const teamName = (id: string) => w.device.data?.teams?.find((t) => t.id === id)?.name;
  const bySession = new Map<string, RemoteSession>();
  for (const r of remote) if (r.sessionId) bySession.set(r.sessionId, r);

  if (a?.kind === 'overview') {
    for (const root of a.overview.roots) {
      for (const m of root.members) {
        const r = bySession.get(m.sessionId);
        sessions.push({
          key: `s:${m.sessionId}`,
          title: r?.title || m.name,
          line: [m.teamId === 'solo' ? undefined : teamName(m.teamId), m.task, root.name].filter(Boolean).join(' · '),
          state: STATE_OF[m.status] ?? 'idle',
          words: m.statusWords,
          rootId: root.rootId,
          waiting: m.waitingOn ? 1 : 0,
          ...(r ? { remote: r } : {}),
        });
      }
    }
    for (const n of a.overview.needsYou) {
      needsYou.push({
        id: n.id,
        kind: n.kind,
        title: n.title || (n.kind === 'approval' ? 'Use a tool' : n.kind === 'question' ? 'A question' : 'Work to review'),
        line: [n.member, n.rootName].filter(Boolean).join(' · '),
        ...(n.sessionId ? { sessionKey: `s:${n.sessionId}`, sessionId: n.sessionId } : {}),
        ...(n.riskLevel ? { riskLevel: n.riskLevel } : {}),
      });
    }
  } else if (a?.kind === 'basic') {
    for (const s of a.sessions) {
      const r = bySession.get(s.sessionId);
      const waiting = a.permissions.filter((p) => p.sessionId === s.sessionId).length;
      sessions.push({
        key: `s:${s.sessionId}`,
        title: r?.title || s.sessionTitle || s.name,
        line: [s.sessionTitle && s.sessionTitle !== s.name ? s.name : undefined, s.teamName, s.currentTool ? `using ${s.currentTool}` : undefined].filter(Boolean).join(' · '),
        state: STATE_OF[s.status] ?? 'idle',
        words: titleCase(s.status === 'tool_calling' ? 'calling tools' : s.status === 'waiting_approval' ? 'needs approval' : s.status),
        waiting,
        ...(r ? { remote: r } : {}),
      });
    }
    for (const p of a.permissions) {
      needsYou.push({ id: p.permissionId, kind: 'approval', title: p.title ?? p.kind ?? 'Use a tool', line: [p.agentName, p.teamName].filter(Boolean).join(' · '), sessionKey: `s:${p.sessionId}`, sessionId: p.sessionId });
    }
  }

  // Conversations open to remote control (Chat, Research, Team Work) and sessions the machine did not list just now.
  const listed = new Set(sessions.map((s) => s.key));
  for (const r of remote) {
    const key = liveKey(r);
    if (!key || listed.has(key)) continue;
    sessions.push({
      key,
      title: r.title || MODE[r.mode] || 'Session',
      line: MODE[r.mode] ?? 'Session',
      state: r.status ? (REMOTE_STATE[r.status] ?? 'idle') : 'idle',
      words: r.status ? (REMOTE_WORDS[r.status] ?? titleCase(r.status)) : 'Open to remote control',
      remote: r,
      waiting: r.status === 'waiting' ? 1 : 0,
      ...(r.rootId ? { rootId: r.rootId } : {}),
    });
  }

  const rank = (s: MachineSession) => (s.waiting ? 0 : s.state === 'working' ? 1 : s.remote ? 2 : 3);
  sessions.sort((x, y) => rank(x) - rank(y) || x.title.localeCompare(y.title));
  return {
    data: { sessions, needsYou, working: sessions.filter((s) => s.state === 'working').length, live: Boolean(a) },
    error: w.connected ? answer.error : undefined,
    loading: answer.loading,
    reload: answer.reload,
  };
}
